import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const useQueryState = <T>(
  key: string,
  defaultValue: T,
  parse: (value: string) => T = JSON.parse,
  stringify: (value: T) => string = JSON.stringify
): [T, (newValue: T) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryValue = searchParams.get(key);
  const parsedValue = initialQueryValue
    ? parse(initialQueryValue)
    : defaultValue;
  const [state, setState] = useState<T>(parsedValue);

  useEffect(() => {
    const queryValue = searchParams.get(key);
    setState(queryValue ? parse(queryValue) : defaultValue);
  }, [searchParams, key, defaultValue, parse]);

  const setQueryState = useCallback(
    (newValue: T) => {
      setState(newValue);
      const newParams = new URLSearchParams(searchParams);
      if (newValue && Object.keys(newValue).length !== 0) {
        newParams.set(key, stringify(newValue));
      } else {
        newParams.delete(key);
      }
      setSearchParams(newParams, { replace: true });
    },
    [key, searchParams, setSearchParams, stringify]
  );

  return [state, setQueryState];
};

export default useQueryState;
